import { isWeightRelatedLockError, mapLockErrorCode } from 'services/rating-matrix-framework-template/lock-errors';
import { requestError } from 'AurionCR/components/helpers';

/** B-01 — 400 raw-string codes of the ballot endpoints; each maps to `ballot-error-<code>`. */
export const BALLOT_ERROR_CODES = [
  'voter-position-not-voting',
  'voter-conflict-of-interest',
  'template-not-ballot-capable',
  'committee-chair-locked',
  'link-not-primary',
  'vote-justification-required',
  'vote-rating-invalid',
  'ballot-rating-missing',
  'ballot-outlook-missing',
  'qual-justification-required',
  'caller-not-committee-chair',
  'chair-position-not-resolved',
  'ballots-not-all-submitted',
  'committee-no-verdict',
  'committee-not-chair-locked',
  'vote-owned-by-ballot',
  'link-has-ballots',
  'template-has-ballots',
] as const;

export type BallotErrorCode = (typeof BALLOT_ERROR_CODES)[number];

export const BALLOT_GENERIC_ERROR = 'frameworks-list-toast-error-generic';
const NOT_ENOUGH_PERMISSIONS = 'NOT_ENOUGH_PERMISSIONS';

/** Refused by the backend once a primary link exists; the handoff gives it no label. */
const CHAIR_LOCK_USE_ENDPOINT = 'chair-lock-use-endpoint';

export type ParsedBallotError = {
  status: number | null;
  /** Text before the first `|` (the whole body when there is none). */
  code: string | null;
  /** `sub=<id>` from the payload — the row to highlight. */
  subParameterId: number | null;
  raw: string;
};

function bodyOf(error: unknown): { status: number | null; raw: string } {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data;
  let raw = '';
  if (typeof data === 'string') {
    raw = data;
  } else if (data && typeof data === 'object' && 'message' in data) {
    const message = (data as { message?: unknown }).message;
    raw = typeof message === 'string' ? message : '';
  }
  return { status: response?.status ?? null, raw: raw.trim() };
}

export function parseBallotError(error: unknown): ParsedBallotError {
  const { status, raw } = bodyOf(error);
  const [head, ...segments] = raw.split('|');
  const subSegment = segments.find((segment) => segment.trim().startsWith('sub='));
  const subValue = subSegment ? Number(subSegment.trim().slice('sub='.length)) : NaN;
  return {
    status,
    code: head?.trim() || null,
    subParameterId: Number.isFinite(subValue) ? subValue : null,
    raw,
  };
}

export function isBallotErrorCode(code: string | null): code is BallotErrorCode {
  return code != null && (BALLOT_ERROR_CODES as readonly string[]).includes(code);
}

export function ballotErrorLabelKey(code: BallotErrorCode): string {
  return `ballot-error-${code}`;
}

/**
 * B-01 — toast text for a ballot screen request: 401 → its label, a known code → its label,
 * a template lock error (first save of a chip) → the lock pre-flight label, else generic.
 */
export function ballotErrorMessage(error: unknown, t: (key: string) => string): string {
  const parsed = parseBallotError(error);
  if (parsed.status === 401) {
    return t(parsed.raw || NOT_ENOUGH_PERMISSIONS);
  }
  if (isBallotErrorCode(parsed.code)) {
    return t(ballotErrorLabelKey(parsed.code));
  }
  if (parsed.raw && isWeightRelatedLockError(parsed.raw)) {
    return mapLockErrorCode(parsed.raw, t);
  }
  return t(BALLOT_GENERIC_ERROR);
}

/**
 * B-01 — for the existing committee screens (voting table, series votes, chair checkbox,
 * "המשך בתהליך", matrix chips): the label key of a ballot refusal, or null to keep the
 * screen's own error handling.
 */
export function ballotRefusalLabelKey(error: unknown): string | null {
  const { code, status } = parseBallotError(error);
  if (status === 401) {
    return null;
  }
  if (isBallotErrorCode(code)) {
    return ballotErrorLabelKey(code);
  }
  if (code === CHAIR_LOCK_USE_ENDPOINT) {
    return BALLOT_GENERIC_ERROR;
  }
  return null;
}

/**
 * B-01 — drop-in for `requestError(e)` in the committee-step sagas: a ballot refusal shows its
 * label, any other error keeps the previous message.
 */
export function ballotAwareRequestError(error: unknown): string {
  return ballotRefusalLabelKey(error) ?? requestError(error);
}
